import workletUrl from "../audio/worklet.ts?worker&url";
import { AUDIO_SAMPLE_RATE } from "../worker/protocol";

/**
 * Plays worker audio through an AudioWorklet ring buffer and relays its buffer level
 * back to the worker for pacing (§7.3). If autoplay is blocked, the worker falls back
 * to clock pacing until `resume()` succeeds after a user gesture.
 */
export class AudioOut {
  private ctx: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private gain: GainNode | null = null;
  onLevel: (bufferedFrames: number) => void = () => {};

  async start(): Promise<void> {
    if (this.ctx) return;
    try {
      const ctx = new AudioContext({ sampleRate: AUDIO_SAMPLE_RATE, latencyHint: "interactive" });
      await ctx.audioWorklet.addModule(workletUrl);
      const node = new AudioWorkletNode(ctx, "redchain-ring", { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
      const gain = ctx.createGain();
      gain.gain.value = this.volume;
      node.connect(gain).connect(ctx.destination);
      node.port.onmessage = (ev: MessageEvent<{ type: "level"; bufferedFrames: number }>) => {
        if (ev.data?.type === "level" && ctx.state === "running") this.onLevel(ev.data.bufferedFrames);
      };
      this.ctx = ctx;
      this.node = node;
      this.gain = gain;
      if (ctx.state !== "running") {
        const unlock = () => {
          void ctx.resume();
          window.removeEventListener("pointerdown", unlock);
          window.removeEventListener("keydown", unlock);
        };
        window.addEventListener("pointerdown", unlock);
        window.addEventListener("keydown", unlock);
      }
    } catch (e) {
      console.warn("Audio unavailable; pacing with the clock", e);
      this.ctx = null;
    }
  }

  push(samples: Float32Array): void {
    if (!this.node || this.ctx?.state !== "running") return;
    this.node.port.postMessage(samples, [samples.buffer]);
  }

  private volume = Number(localStorage.getItem("redchain.volume") ?? "0.8");

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    localStorage.setItem("redchain.volume", String(this.volume));
    if (this.gain) this.gain.gain.value = this.volume;
  }

  getVolume(): number {
    return this.volume;
  }

  async stop(): Promise<void> {
    this.node?.disconnect();
    await this.ctx?.close().catch(() => {});
    this.ctx = null;
    this.node = null;
  }
}
